import { useRef, useCallback, type KeyboardEvent } from 'react'
import { useEditContext } from '@/contexts/EditContext'
import { cn } from '@/lib/utils'

interface EditableTextProps {
  value: string
  onChange: (value: string) => void
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'div'
  className?: string
  placeholder?: string
  multiline?: boolean
}

export function EditableText({
  value,
  onChange,
  as: Component = 'span',
  className,
  placeholder = 'Cliquez pour editer...',
  multiline = false,
}: EditableTextProps) {
  const { isEditMode } = useEditContext()
  const elementRef = useRef<HTMLElement>(null)

  const handleBlur = useCallback(() => {
    if (elementRef.current) {
      const newValue = elementRef.current.textContent || ''
      if (newValue !== value) {
        onChange(newValue)
      }
    }
  }, [value, onChange])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!multiline && e.key === 'Enter') {
        e.preventDefault()
        elementRef.current?.blur()
      }
    },
    [multiline]
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, multiline ? text : text.replace(/\n/g, ' '))
    },
    [multiline]
  )

  if (!isEditMode) {
    return (
      <Component className={className}>
        {value || placeholder}
      </Component>
    )
  }

  return (
    <Component
      ref={elementRef as React.RefObject<HTMLElement & HTMLDivElement>}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      className={cn(
        className,
        'outline-none transition-all',
        'hover:ring-2 hover:ring-primary-300 hover:ring-offset-2',
        'focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        'rounded-sm cursor-text',
        !value && 'text-gray-400'
      )}
      data-placeholder={placeholder}
    >
      {value || placeholder}
    </Component>
  )
}
